/**
 * Tiny localStorage-backed store for collapsed workspace groups in the CMMD
 * project pickers (import palette + GitHub planning-context step).
 *
 * Persisted as a JSON array of *collapsed* workspace ids, so an absent, empty,
 * or corrupt key means "everything expanded" — the default the pickers want.
 * Falls back to no-op on environments without localStorage (e.g. SSR/preview)
 * instead of throwing.
 */

import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "forge.cmmdProjectPicker.collapsedWorkspaces.v1";

type CollapsedSnapshot = ReadonlyArray<string>;

let snapshot: CollapsedSnapshot = readFromStorage();
const listeners = new Set<() => void>();

function readFromStorage(): CollapsedSnapshot {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === "string");
  } catch {
    return [];
  }
}

function writeToStorage(next: CollapsedSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota or storage disabled — degrade silently. The in-memory snapshot
    // still reflects the toggle for the current session.
  }
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function getCollapsedCmmdWorkspacesSnapshot(): CollapsedSnapshot {
  return snapshot;
}

export function toggleCollapsedCmmdWorkspace(workspaceId: string): void {
  const collapsed = snapshot.includes(workspaceId);
  snapshot = collapsed
    ? snapshot.filter((entry) => entry !== workspaceId)
    : [...snapshot, workspaceId];
  writeToStorage(snapshot);
  notify();
}

export function subscribeCollapsedCmmdWorkspaces(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useCollapsedCmmdWorkspaces(): {
  collapsed: ReadonlySet<string>;
  isCollapsed: (workspaceId: string) => boolean;
  toggle: (workspaceId: string) => void;
} {
  const current = useSyncExternalStore(
    subscribeCollapsedCmmdWorkspaces,
    getCollapsedCmmdWorkspacesSnapshot,
    getCollapsedCmmdWorkspacesSnapshot,
  );
  const set = useCollapsedSet(current);
  const toggle = useCallback((workspaceId: string) => {
    toggleCollapsedCmmdWorkspace(workspaceId);
  }, []);
  const isCollapsed = useCallback((workspaceId: string) => set.has(workspaceId), [set]);
  return { collapsed: set, isCollapsed, toggle };
}

// Memoize the Set construction across renders that don't change the array
// identity. useSyncExternalStore guarantees referential stability when the
// snapshot is unchanged, so we can cache one Set per snapshot.
const setCache = new WeakMap<CollapsedSnapshot, ReadonlySet<string>>();
function useCollapsedSet(current: CollapsedSnapshot): ReadonlySet<string> {
  let cached = setCache.get(current);
  if (!cached) {
    cached = new Set(current);
    setCache.set(current, cached);
  }
  return cached;
}
